/**
 * WAT-11878: canonical package-name normaliser used by cmprPkg matching in both
 * the initializer and the grid. NFD-folds accents so URL short names like
 * `optimomas` match display names like `Óptimo Más™`. A plain
 * `.replace(/[^A-Z0-9]/g, '')` would delete accented letters instead of folding
 * them and the Spanish tiers would never match.
 *
 * The combining-mark strip uses `̀-ͯ` (the Combining Diacritical
 * Marks Unicode block) constructed via `RegExp` so the source file itself
 * contains no invisible combining characters — a literal `/[̀-ͯ]/`
 * gets minified/edited into runes that don't survive some editors' round-trip.
 */
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");

export const normalizePkgName = (s?: string): string => (s ?? "").toLowerCase().normalize("NFD").replace(COMBINING_MARKS, "").replace(/[^a-z0-9]/g, "");

/**
 * WAT-11878: read a channel's inclusion indicator for the given package,
 * respecting the package's OWN language slot. `channel.packageIndicators` is
 * keyed as `[period][categoryUpc]` (e.g. `["2yr"]["ENGLISH"]`) and each slot
 * is a dense array indexed by the package's `packageIndicatorIndex` WITHIN
 * that language group. Before this helper the grid read every package's
 * indicator out of the CURRENT view's language slot — for the cmprPkg-injected
 * Óptimo Más column (which carries its Spanish `packageIndicatorIndex`) that
 * meant reading Choice's check mark from the English array.
 *
 * The DDS `Package` class exposes the language on `.category` (see
 * `ChannelClasses.d.ts` in the provider package — the raw feed's `categoryId`
 * is mapped to `.category` by `ChannelLineUpProvider.getFromCmsFeed`).
 *
 * `fallbackCategoryUpc` is the current view's `pkgCtgySelected_upc` — used
 * when the package has no explicit `.category` so the pre-existing lookup is
 * preserved.
 *
 * `source` selects which indicator map to read: `"included"` reads
 * `channel.packageIndicators` (the Included/base columns), `"addon"` reads
 * `channel.addOnPkgIndicators` (the Add-on / Included Subscriptions rows).
 * These are distinct fields on the channel — reading `packageIndicators` for
 * an add-on row would render every column as not-included.
 */
export const getPkgIndicator = (
  channel: any,
  pkg: { category?: string; packageIndicatorIndex?: number },
  edspFlow: boolean,
  fallbackCategoryUpc: string,
  source: "included" | "addon" = "included"
): any => {
  const period = edspFlow ? "edsp" : "2yr";
  const pkgCategoryUpc = pkg?.category?.toUpperCase?.() || fallbackCategoryUpc;
  const map = source === "addon" ? channel?.addOnPkgIndicators : channel?.packageIndicators;
  const indicators = map?.[period]?.[pkgCategoryUpc] || [];
  return indicators[pkg?.packageIndicatorIndex ?? -1];
};

/**
 * WAT-11878: pure helper the grid uses to resolve the cmprPkg column and its
 * border-style key. Extracted so the name-fallback branch (accent-folded match
 * on `pkg.name` when `billingCodeLabel` is missing) can be unit-tested without
 * shallow-rendering the 5000-line grid.
 *
 * - `cmprPkg` is already uppercased upstream (URL short-name → UC).
 * - `packages[i].billingCodeLabel === cmprPkg` (uppercased) wins first —
 *   preserves the pre-WAT-11878 behavior for the English tiers.
 * - Otherwise the normalized name is compared (folds Óptimo Más™ →
 *   `optimomas`) so `?cmprPkg=optimomas` matches the Spanish tier.
 * - The colour-map key is derived from the MATCHED package's name so a
 *   name-fallback match still resolves in `packageColorMap`; a fallback to
 *   the raw `cmprPkg` covers the (pre-WAT-11878) English path.
 */
export const resolveCmprPkgSelection = (
  cmprPkgUpper: string,
  packages: Array<{ billingCodeLabel?: string; name?: string }>,
  packageColorMap: Record<string, string>
): {
  index: number;
  packageColorMapValue: string | undefined;
} => {
  if (!cmprPkgUpper || !Array.isArray(packages)) {
    return {
      index: -1,
      packageColorMapValue: undefined
    };
  }
  const cmprPkgKey = normalizePkgName(cmprPkgUpper);
  const index = packages.findIndex(pkg => {
    if (cmprPkgUpper === pkg?.billingCodeLabel?.toUpperCase()) {
      return true;
    }
    return !!cmprPkgKey && cmprPkgKey === normalizePkgName(pkg?.name);
  });
  if (index === -1) {
    return {
      index,
      packageColorMapValue: undefined
    };
  }
  const matchedName = packages[index]?.name;
  const colorMapKey = normalizePkgName(matchedName).toUpperCase() || cmprPkgUpper;
  const packageColorMapValue = packageColorMap?.[colorMapKey] ?? packageColorMap?.[cmprPkgUpper];
  return {
    index,
    packageColorMapValue
  };
};

export const getPackageColors = (
  packageName: string,
  bgColors: any,
  pkgColors: any
) => {
  // eslint-disable-next-line camelcase
  const packageName_lwrc = packageName?.toLowerCase();
  let bgColor = "";
  let pkgColor = "";

  for (const item of Object.keys(bgColors)) {
    // eslint-disable-next-line camelcase
    if (packageName_lwrc.includes(item.toLowerCase())) {
      bgColor = item;
      break;
    }
  }

  for (const item of Object.keys(pkgColors)) {
    // eslint-disable-next-line camelcase
    if (packageName_lwrc.includes(item.toLowerCase())) {
      pkgColor = item;
      break;
    }
  }

  return {
    packageBgColor: bgColors[bgColor] || "",
    packageTextColor: pkgColors[pkgColor] || ""
  };
};

export const getPackageCta = (
  packageData,
  modalSrc,
  modalView,
  modalWithNOCTA,
  showCTA: string,
  showCtaCI: string
) => {
  const {
    cta,
    ufCTA,
    salesCTA,
    internetCTA,
    satelliteCTA,
    supportInternetCTA,
    supportSatelliteCTA,
    servicesInternetCTA,
    servicesSatelliteCTA
  } = packageData;

  /** Do CTA Mapping */
  const ctaMapping = {
    UF: ufCTA,
    SALES: salesCTA,
    INTERNET: internetCTA,
    SATELLITE: satelliteCTA,
    "SUPPORT-INTERNET": supportInternetCTA,
    "SUPPORT-SATELLITE": supportSatelliteCTA,
    "SERVICES-INTERNET": servicesInternetCTA,
    "SERVICES-SATELLITE": servicesSatelliteCTA
  };
  // Flag-ON (channels-service) packages carry only `ufCTA` (re-homed from
  // package-config `array_packageConfig[].cta`); the modalSrc-specific keys
  // (satelliteCTA/internetCTA/…) are AEM-only and absent there. Fall back to
  // ufCTA so the Shop CTA renders flag-ON; AEM modalSrc keys still win when present.
  const modalCTA = ctaMapping?.[modalSrc] || ufCTA || cta;

  /** Check if we should display a Cta */
  const isSupportModal = modalSrc === "SUPPORT-INTERNET" || modalSrc === "SUPPORT-SATELLITE";
  const hasValidButtonContent = modalCTA && modalCTA?.props?.text !== "";
  let displayPkgCta = !!modalSrc;

  if (isSupportModal) {
    displayPkgCta = hasValidButtonContent;
  } else if (modalView && !modalWithNOCTA) {
    displayPkgCta = true;
  }

  if (showCtaCI) {
    displayPkgCta = showCtaCI?.toUpperCase() !== "FALSE";
  } else {
    displayPkgCta = !!modalSrc;
  }

  if (showCTA) {
    displayPkgCta = showCTA !== "FALSE";
  }

  return {
    displayPkgCta,
    modalCTA
  };
}
